import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  alpha,
  useTheme,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import { FilterAltOff as ClearFilterIcon } from '@mui/icons-material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PostAdd as PostIcon,
  Undo as UndoIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useConfirm } from '../hooks/useConfirm';
import PurchaseInvoiceDialog from '../components/purchaseInvoices/PurchaseInvoiceDialog';
import PurchaseInvoicePreviewDialog from '../components/purchaseInvoices/PurchaseInvoicePreviewDialog';
import { PurchaseInvoice } from '../types/purchaseInvoices.types';
import { purchaseInvoicesService } from '../services/purchaseInvoices.service';

const translations = {
  en: {
    title: 'Purchase Invoices',
    addInvoice: 'New Invoice',
    search: 'Search invoices...',
    all: 'All',
    draft: 'Draft',
    posted: 'Posted',
    totalInvoices: 'Total Invoices',
    totalAmount: 'Total Amount',
    unpaidAmount: 'Unpaid',
    supplier: 'Supplier',
    warehouse: 'Warehouse',
    date: 'Date',
    amount: 'Amount',
    paid: 'Paid',
    balance: 'Balance',
    status: 'Status',
    paymentSource: 'Payment Source',
    custody: 'Custody',
    paymentAccount: 'Payment Account',
    filterByWarehouse: 'Filter by Warehouse',
    filterByPayment: 'Payment Method',
    allPayments: 'All',
    actions: 'Actions',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    post: 'Post Invoice',
    unpost: 'Unpost',
    noData: 'No invoices found',
    confirmDelete: 'Delete this invoice?',
    confirmPost: 'Post this invoice? Stock and accounting will be affected.',
    confirmUnpost: 'Unpost this invoice? This will reverse all effects.',
    journalEntry: 'J.E.',
  },
  ar: {
    title: 'فواتير الشراء',
    addInvoice: 'فاتورة جديدة',
    search: 'بحث في الفواتير...',
    all: 'الكل',
    draft: 'مسودة',
    posted: 'مرحّل',
    totalInvoices: 'إجمالي الفواتير',
    totalAmount: 'إجمالي المبالغ',
    unpaidAmount: 'غير مدفوع',
    supplier: 'المورد',
    warehouse: 'المخزن',
    date: 'التاريخ',
    amount: 'المبلغ',
    paid: 'المدفوع',
    balance: 'المتبقي',
    status: 'الحالة',
    paymentSource: 'مصدر الدفع',
    custody: 'عهدة',
    paymentAccount: 'حساب مباشر',
    filterByWarehouse: 'فلتر المخزن',
    filterByPayment: 'طريقة الدفع',
    allPayments: 'الكل',
    actions: 'الإجراءات',
    view: 'عرض',
    edit: 'تعديل',
    delete: 'حذف',
    post: 'ترحيل',
    unpost: 'إلغاء ترحيل',
    noData: 'لا توجد فواتير',
    confirmDelete: 'حذف هذه الفاتورة؟',
    confirmPost: 'ترحيل الفاتورة؟ سيتم التأثير على المخزون والمحاسبة.',
    confirmUnpost: 'إلغاء الترحيل؟ سيتم عكس جميع التأثيرات.',
    journalEntry: 'ق.م.',
  },
};

const PurchaseInvoicesPage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const confirm = useConfirm();

  const { userBranchIds, userWarehouseIds } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const keeperClaims = payload.KeeperWarehouseId;
      const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
      return { userBranchIds: branchIds, userWarehouseIds: warehouseIds };
    } catch {
      return { userBranchIds: [] as number[], userWarehouseIds: [] as number[] };
    }
  })();
  
  const canEditInvoice = (invoice: PurchaseInvoice) => {
    // SuperAdmin/Admin/ChiefAccountant يرون كل شيء
    if (userBranchIds.length === 0 && userWarehouseIds.length === 0) return true;
    // أمين المخزن يرى المخازن التي يديرها
    if (invoice.warehouseId && userWarehouseIds.includes(invoice.warehouseId)) return true;
    // مدير الفرع يرى مخازن فروعه (إذا كان المخزن تابع لفرع)
    if (invoice.branchId && userBranchIds.includes(invoice.branchId)) return true;
    return false;
  };
  
  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<PurchaseInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterPaymentType, setFilterPaymentType] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<PurchaseInvoice | undefined>();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoiceForMenu, setSelectedInvoiceForMenu] = useState<PurchaseInvoice | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [invoiceToPreview, setInvoiceToPreview] = useState<PurchaseInvoice | null>(null);

  useEffect(() => {
    loadInvoices();
  }, []);

  // ✅ Auto-open invoice preview when ?openId=X is in the URL (used by Data Integrity report)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('openId');
    if (!openId) return;
    const id = parseInt(openId, 10);
    if (!Number.isFinite(id)) return;
    (async () => {
      try {
        const full = await purchaseInvoicesService.getById(id);
        setInvoiceToPreview(full);
        setPreviewDialogOpen(true);
      } catch {
        toast.error('Failed to open invoice');
      }
    })();
  }, []);

  const suppliers = useMemo(() => {
    const map = new Map<string, string>();
    invoices.forEach(inv => { if (inv.supplierName) map.set(String(inv.supplierId || inv.supplierName), inv.supplierName); });
    return Array.from(map.entries());
  }, [invoices]);

  const warehouses = useMemo(() => {
    const map = new Map<number, string>();
    invoices.forEach(inv => { if (inv.warehouseId && inv.warehouseName) map.set(inv.warehouseId, inv.warehouseName); });
    return Array.from(map.entries());
  }, [invoices]);

  useEffect(() => {
    filterInvoices();
  }, [searchTerm, filterSupplier, filterDateFrom, filterDateTo, filterWarehouse, filterPaymentType, invoices, activeTab]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = await purchaseInvoicesService.getAll();
      const filteredData = data
        .filter((invoice: PurchaseInvoice) => canEditInvoice(invoice))
        .sort((a: PurchaseInvoice, b: PurchaseInvoice) => b.purchaseInvoiceId - a.purchaseInvoiceId);
      setInvoices(filteredData);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = invoices;

    // Filter by tab
    if (activeTab === 1) {
      filtered = filtered.filter(inv => inv.status === 'Draft');
    } else if (activeTab === 2) {
      filtered = filtered.filter(inv => inv.status === 'Posted');
    }

    // Filter by search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        inv =>
          inv.invoiceNumber.toLowerCase().includes(term) ||
          inv.supplierName?.toLowerCase().includes(term) ||
          inv.supplierNameAr?.toLowerCase().includes(term) ||
          inv.custodyNumber?.toLowerCase().includes(term) ||
          inv.employeeName?.toLowerCase().includes(term) ||
          inv.paymentAccountName?.toLowerCase().includes(term)
      );
    }

    // Filter by supplier
    if (filterSupplier) {
      filtered = filtered.filter(inv => String(inv.supplierId || inv.supplierName) === filterSupplier);
    }

    // Filter by warehouse
    if (filterWarehouse) {
      filtered = filtered.filter(inv => inv.warehouseId === Number(filterWarehouse));
    }

    // Filter by payment type
    if (filterPaymentType === 'supplier') {
      filtered = filtered.filter(inv => inv.supplierId && !inv.custodyId && !inv.paymentAccountId);
    } else if (filterPaymentType === 'custody') {
      filtered = filtered.filter(inv => inv.custodyId);
    } else if (filterPaymentType === 'account') {
      filtered = filtered.filter(inv => inv.paymentAccountId);
    }

    // Filter by date range
    if (filterDateFrom) {
      filtered = filtered.filter(inv => inv.invoiceDate >= filterDateFrom);
    }
    if (filterDateTo) {
      filtered = filtered.filter(inv => inv.invoiceDate <= filterDateTo);
    }

    setFilteredInvoices(filtered);
  };

  const handleAdd = () => {
    setSelectedInvoice(undefined);
    setDialogOpen(true);
  };

  const handleView = async (invoice: PurchaseInvoice) => {
    handleCloseMenu();
    try {
      // Fetch full invoice with details
      const fullInvoice = await purchaseInvoicesService.getById(invoice.purchaseInvoiceId);
      setInvoiceToPreview(fullInvoice);
      setPreviewDialogOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load invoice details');
    }
  };

  const handleEdit = (invoice: PurchaseInvoice) => {
    setSelectedInvoice(invoice);
    setDialogOpen(true);
    handleCloseMenu();
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) { handleCloseMenu(); return; }
    try {
      await purchaseInvoicesService.delete(id);
      loadInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
    handleCloseMenu();
  };

  const handlePost = async (id: number) => {
    const ok = await confirm({ message: t.confirmPost, severity: 'warning' });
    if (!ok) { handleCloseMenu(); return; }
    try {
      await purchaseInvoicesService.postInvoice(id);
      loadInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to post');
    }
    handleCloseMenu();
  };

  const handleUnpost = async (id: number) => {
    const ok = await confirm({ message: t.confirmUnpost, severity: 'warning' });
    if (!ok) { handleCloseMenu(); return; }
    try {
      await purchaseInvoicesService.unpostInvoice(id);
      loadInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unpost');
    }
    handleCloseMenu();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, invoice: PurchaseInvoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoiceForMenu(invoice);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedInvoiceForMenu(null);
  };

  const calculateStats = () => {
    const total = invoices.length;
    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
    const unpaidAmount = invoices.reduce((sum, inv) => sum + (inv.remainingAmount || 0), 0);
    const draftCount = invoices.filter(inv => inv.status === 'Draft').length;
    const postedCount = invoices.filter(inv => inv.status === 'Posted').length;

    return { total, totalAmount, unpaidAmount, draftCount, postedCount };
  };

  const stats = calculateStats();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 56,
              height: 56,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <ReceiptIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {t.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.total} {language === 'ar' ? 'فاتورة' : 'invoices'}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadInvoices} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
              px: 4,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(45deg, #5568d3 30%, #6a4190 90%)',
              },
            }}
          >
            {t.addInvoice}
          </Button>
        </Box>
      </Stack>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -20,
              right: -20,
              width: 100,
              height: 100,
              background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
              borderRadius: '50%',
            },
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t.totalInvoices}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.total}
                </Typography>
              </Box>
              <ShoppingCartIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t.totalAmount}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {stats.totalAmount.toFixed(3)}
                </Typography>
                <Typography variant="caption">KD</Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t.unpaidAmount}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {stats.unpaidAmount.toFixed(3)}
                </Typography>
                <Typography variant="caption">KD</Typography>
              </Box>
              <MoneyIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {t.status}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {stats.draftCount}
                </Typography>
                <Typography variant="caption">{t.draft}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {stats.postedCount}
                </Typography>
                <Typography variant="caption">{t.posted}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper
        elevation={3}
        sx={{
          mb: 3,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)'
            : 'linear-gradient(135deg, #e0e7ff 0%, #cffafe 100%)',
          borderRadius: 3,
        }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />

          <Paper sx={{ p: 2, mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t.supplier}</InputLabel>
              <Select value={filterSupplier} label={t.supplier} onChange={(e) => setFilterSupplier(e.target.value)}
                onClose={() => setSupplierSearch('')}
                MenuProps={{ PaperProps: { sx: { maxHeight: 350 } }, autoFocus: false }}>
                <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                  <TextField size="small" fullWidth placeholder={isRTL ? 'بحث...' : 'Search...'} value={supplierSearch}
                    onChange={e => setSupplierSearch(e.target.value)} autoFocus onKeyDown={e => e.stopPropagation()}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                </Box>
                <MenuItem value="">{isRTL ? 'جميع الموردين' : 'All Suppliers'}</MenuItem>
                {suppliers.filter(([, name]) => !supplierSearch || name.toLowerCase().includes(supplierSearch.toLowerCase())).map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t.filterByWarehouse}</InputLabel>
              <Select value={filterWarehouse} label={t.filterByWarehouse} onChange={(e) => setFilterWarehouse(e.target.value)}
                onClose={() => setWarehouseSearch('')}
                MenuProps={{ PaperProps: { sx: { maxHeight: 350 } }, autoFocus: false }}>
                <Box sx={{ px: 1, py: 0.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                  <TextField size="small" fullWidth placeholder={isRTL ? 'بحث...' : 'Search...'} value={warehouseSearch}
                    onChange={e => setWarehouseSearch(e.target.value)} autoFocus onKeyDown={e => e.stopPropagation()}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                </Box>
                <MenuItem value="">{t.allPayments}</MenuItem>
                {warehouses.filter(([, name]) => !warehouseSearch || name.toLowerCase().includes(warehouseSearch.toLowerCase())).map(([id, name]) => <MenuItem key={id} value={String(id)}>{name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t.filterByPayment}</InputLabel>
              <Select value={filterPaymentType} label={t.filterByPayment} onChange={(e) => setFilterPaymentType(e.target.value)}>
                <MenuItem value="">{t.allPayments}</MenuItem>
                <MenuItem value="supplier">{t.supplier}</MenuItem>
                <MenuItem value="custody">{t.custody}</MenuItem>
                <MenuItem value="account">{t.paymentAccount}</MenuItem>
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={isRTL ? 'من تاريخ' : 'From Date'}
                value={filterDateFrom ? new Date(filterDateFrom) : null}
                onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateFrom(v); }}
                format="dd/MM/yyyy" minDate={minDate} maxDate={filterDateTo ? new Date(filterDateTo) : maxDate}
                views={['year', 'month', 'day']} openTo="day"
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
              <DatePicker
                label={isRTL ? 'إلى تاريخ' : 'To Date'}
                value={filterDateTo ? new Date(filterDateTo) : null}
                onChange={(date) => { const v = date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''; setFilterDateTo(v); }}
                format="dd/MM/yyyy" minDate={filterDateFrom ? new Date(filterDateFrom) : minDate} maxDate={maxDate}
                views={['year', 'month', 'day']} openTo="day"
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
            </LocalizationProvider>

            {(filterSupplier || filterDateFrom || filterDateTo || filterWarehouse || filterPaymentType) && (
              <Button size="small" startIcon={<ClearFilterIcon />} onClick={() => { setFilterSupplier(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterWarehouse(''); setFilterPaymentType(''); }}>
                {isRTL ? 'مسح' : 'Clear'}
              </Button>
            )}
          </Paper>

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              },
              '& .Mui-selected': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <Tab
              label={
                <Badge badgeContent={stats.total} color="primary" max={999}>
                  <span>{t.all}</span>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={stats.draftCount} color="warning" max={999}>
                  <span>{t.draft}</span>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={stats.postedCount} color="success" max={999}>
                  <span>{t.posted}</span>
                </Badge>
              }
            />
          </Tabs>
        </Stack>
      </Paper>

      {/* Invoices List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {filteredInvoices.map((invoice) => (
              <Card
                key={invoice.purchaseInvoiceId}
                elevation={3}
                sx={{
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8,
                  },
                  border: 2,
                  borderColor: invoice.status === 'Posted' ? 'success.light' : 'warning.light',
                  borderLeft: 6,
                  borderLeftColor: invoice.status === 'Posted' ? 'success.main' : 'warning.main',
                  background: theme.palette.mode === 'dark' 
                    ? alpha('#1e293b', 0.8)
                    : '#ffffff',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    {/* Invoice Header */}
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: invoice.status === 'Posted' ? 'success.main' : 'warning.main',
                        }}
                      >
                        <ReceiptIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {invoice.invoiceNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(invoice.invoiceDate)}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Status & Menu */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={invoice.status === 'Posted' ? t.posted : t.draft}
                        color={invoice.status === 'Posted' ? 'success' : 'warning'}
                        icon={invoice.status === 'Posted' ? <CheckCircleIcon /> : <ScheduleIcon />}
                        sx={{ fontWeight: 600, minWidth: 90 }}
                      />
                      
                      {invoice.status === 'Posted' && invoice.journalEntryId && (
                        <Chip
                          label={`${t.journalEntry} ${invoice.journalEntryNumber || 'JE'} (#${invoice.journalEntryId})`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}

                      <IconButton onClick={(e) => handleOpenMenu(e, invoice)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Invoice Details */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, 
                    gap: 3 
                  }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t.paymentSource}
                      </Typography>
                      <Typography variant="body1" component="div" sx={{ fontWeight: 600 }}>
                        {invoice.custodyId ? (
                          <Chip label={`${t.custody}: ${invoice.custodyNumber} — ${invoice.employeeName || ''}`} size="small" color="warning" variant="outlined" />
                        ) : invoice.paymentAccountId ? (
                          <Chip label={`${t.paymentAccount}: ${invoice.paymentAccountName || invoice.paymentAccountCode}`} size="small" color="info" variant="outlined" />
                        ) : invoice.supplierName ? (
                          <Chip label={`${t.supplier}: ${isRTL ? invoice.supplierNameAr : invoice.supplierName}`} size="small" color="primary" variant="outlined" />
                        ) : '-'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t.warehouse}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {isRTL ? invoice.warehouseNameAr : invoice.warehouseName}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t.amount}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {invoice.totalAmount?.toFixed(3)} KD
                      </Typography>
                    </Box>

                    <Box>
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">{t.paid}:</Typography>
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            {invoice.paidAmount?.toFixed(3)} KD
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">{t.balance}:</Typography>
                          <Typography variant="body2" color="error.main" fontWeight={700}>
                            {invoice.remainingAmount?.toFixed(3)} KD
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
          ))}

          {filteredInvoices.length === 0 && (
            <Paper
              sx={{
                p: 8,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <ReceiptIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {t.noData}
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={() => selectedInvoiceForMenu && handleView(selectedInvoiceForMenu)}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
          <Typography color="primary.main">{t.view}</Typography>
        </MenuItem>

        <Divider />

        {selectedInvoiceForMenu?.status === 'Draft' && canEditInvoice(selectedInvoiceForMenu) && (
          <MenuItem onClick={() => handleEdit(selectedInvoiceForMenu)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {t.edit}
          </MenuItem>
        )}

        {selectedInvoiceForMenu?.status === 'Draft' && canEditInvoice(selectedInvoiceForMenu) && (
          <MenuItem onClick={() => handleDelete(selectedInvoiceForMenu.purchaseInvoiceId)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography color="error">{t.delete}</Typography>
          </MenuItem>
        )}

        {selectedInvoiceForMenu?.status === 'Draft' && canPost && (
          <MenuItem onClick={() => handlePost(selectedInvoiceForMenu.purchaseInvoiceId)}>
            <PostIcon fontSize="small" sx={{ mr: 1 }} color="success" />
            <Typography color="success.main">{t.post}</Typography>
          </MenuItem>
        )}

        {selectedInvoiceForMenu?.status === 'Posted' && canPost && (
          <MenuItem onClick={() => handleUnpost(selectedInvoiceForMenu.purchaseInvoiceId)}>
            <UndoIcon fontSize="small" sx={{ mr: 1 }} color="warning" />
            <Typography color="warning.main">{t.unpost}</Typography>
          </MenuItem>
        )}
      </Menu>

      <PurchaseInvoiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        purchaseInvoice={selectedInvoice}
        onSave={loadInvoices}
      />

      <PurchaseInvoicePreviewDialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setInvoiceToPreview(null);
        }}
        invoice={invoiceToPreview}
        onEdit={() => {
          if (invoiceToPreview) {
            setPreviewDialogOpen(false);
            handleEdit(invoiceToPreview);
          }
        }}
      />
    </Box>
  );
};

export default PurchaseInvoicesPage;